import React from 'react';
import { Card } from '../../ui/Card';
import ReactMarkdown from 'react-markdown';
import { sb } from '../../../modules/state';
import { useAuth } from '../../../context/AuthContext';

interface BloodReport {
  id: string;
  report: string;
  summary: string | null;
  created_at: string;
}

export function BloodReportView() {
  const { currentUser } = useAuth();
  const [reports, setReports] = React.useState<BloodReport[]>([]);
  const [selectedReport, setSelectedReport] = React.useState<BloodReport | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState<string | null>(null);
  const initialized = React.useRef(false);

  // Load blood reports on mount
  React.useEffect(() => {
    // Prevent re-fetching on subsequent renders
    if (initialized.current) return;
    initialized.current = true;

    const loadReports = async () => {
      if (!sb || !currentUser?.id) return;
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await sb
          .from('blood_reports')
          .select('id, report, summary, created_at')
          .eq('user_id', currentUser.id)
          .not('report', 'is', null)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('[BloodReportView] Error loading reports:', error);
          setError(error.message);
        } else {
          if (data && data.length > 0) {
            setReports(data as BloodReport[]);
            // Select the most recent report by default
            setSelectedReport(data[0] as BloodReport);
          } else {
            setReports([]);
            setSelectedReport(null);
          }
        }
      } catch (err) {
        console.error('[BloodReportView] Unexpected error:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    loadReports();
  }, [currentUser]);

  // Delete a report
  const handleDelete = async (reportId: string) => {
    if (!sb || !currentUser?.id) return;

    try {
      const { error } = await sb
        .from('blood_reports')
        .delete()
        .eq('id', reportId)
        .eq('user_id', currentUser.id);

      if (error) {
        console.error('[BloodReportView] Error deleting report:', error);
        alert('Failed to delete report. Please try again.');
        return;
      }

      // Remove from local state
      setReports(prev => prev.filter(r => r.id !== reportId));
      // Deselect if this was the selected report
      if (selectedReport?.id === reportId) {
        setSelectedReport(null);
      }
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('[BloodReportView] Unexpected error:', err);
      alert('Failed to delete report. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-spin rounded-full border-b-2 border-primary h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
        <span className="material-symbols-outlined text-4xl text-error mb-4">
          error
        </span>
        <p className="text-sm text-on-surface-variant">Error loading reports: {error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 text-sm text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Pane - Report History */}
      <div className="w-80 border-r border-outline bg-surface flex flex-col">
        <div className="p-4 border-b border-outline">
          <h2 className="text-lg font-bold text-on-surface">Blood Reports</h2>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {reports.map((report) => (
            <Card
              key={report.id}
              onClick={() => setSelectedReport(report)}
              className={`p-3 cursor-pointer transition-colors relative ${
                selectedReport?.id === report.id
                  ? 'bg-primary/10 border-primary'
                  : 'hover:bg-surface-container'
              }`}
            >
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-on-surface">
                  {report.created_at ? new Date(report.created_at).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Unknown date'}
                </span>
                <span className="text-xs text-on-surface-variant">
                  {report.created_at ? new Date(report.created_at).toLocaleDateString('en-GB', { year: 'numeric' }) : ''}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(report.id);
                }}
                className="absolute top-2 right-2 p-1 text-on-surface-variant hover:text-error hover:bg-error-container rounded"
                title="Delete report"
              >
                <span className="material-symbols-outlined text-xs">delete</span>
              </button>
            </Card>
          ))}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-surface/80 backdrop-blur-sm">
            <div className="w-full max-w-md bg-surface rounded-xl shadow-2xl p-6">
              <h3 className="text-lg font-bold text-on-surface mb-2">Delete Report</h3>
              <p className="text-sm text-on-surface-variant mb-4">
                Are you sure you want to delete this blood report? This action is permanent and cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 rounded-lg bg-surface-variant text-on-surface hover:bg-surface-variant/80"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="px-4 py-2 rounded-lg bg-error text-error-contrast hover:bg-error/90"
                >
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Pane - Report Content */}
      <div className="flex-1 flex flex-col bg-background">
        <div className="border-b border-outline px-6 py-3 bg-surface flex items-center justify-between">
          <h2 className="text-lg font-semibold text-on-surface">
            {selectedReport?.created_at ? new Date(selectedReport.created_at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Select a report'}
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {selectedReport?.report ? (
            <div className="prose prose-sm max-w-none text-on-surface">
              <ReactMarkdown>{selectedReport.report}</ReactMarkdown>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-12 text-center">
              <span className="material-symbols-outlined text-6xl text-on-surface-variant mb-4">
                description
              </span>
              <h3 className="text-lg font-semibold text-on-surface mb-2">
                No analysis available
              </h3>
              <p className="text-sm text-on-surface-variant max-w-md">
                This blood test does not have an AI-generated analysis yet. Click "DECODE MY BLOOD" on the dashboard to generate an analysis.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
